<template>
    <!-- DoorDash filter bar (merchant portal Video library): the search icon that opens into a
         field, then one pill per filter; phones scroll the pills sideways (tasks#211) -->
    <div
        :class="[
            'dd-filter-bar',
            { 'dd-filter-bar--search-first': searchFirst },
        ]"
    >
        <search-box
            v-if="!hideSearchBox || barSearch"
            v-model="tableFilters"
            class="dd-filter-bar__search"
            :field="barSearch"
            :label="$t('labels.search')"
            :collapsible="!searchFirst"
        ></search-box>
        <div v-if="!hideFilterBox" class="dd-filter-bar__pills">
            <template v-for="(filter, index) in barFilters">
                <chip-filter
                    v-if="filter.inputType === 'checkbox'"
                    :key="filter.key + filter.type + index"
                    v-model="tableFilters"
                    :field="filter"
                ></chip-filter>
                <filter-pill
                    v-else
                    :key="filter.key + filter.type + index"
                    v-model="tableFilters"
                    :field="filter"
                    :close-on-click="filter.inputType !== 'location'"
                    :min-width="pillMinWidth(filter)"
                    :wide="filter.inputType === 'daterange'"
                    :owned-by-chip="chipOwns(filter)"
                >
                    <template #default="{ value: draft, input }">
                        <filter-input
                            :value="draft"
                            :field="filter"
                            @input="input"
                        ></filter-input>
                    </template>
                </filter-pill>
            </template>
            <more-filters
                v-if="advancedFilters.length"
                v-model="tableFilters"
                :fields="advancedFilters"
                :owned-by-chip="chipOwns"
            ></more-filters>
            <v-btn
                v-if="filtered"
                text
                class="dd-filter-bar__clear"
                @click="clearAll"
            >
                {{ clearAllText }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import SearchBox from './SearchBox'
import ChipFilter from './ChipFilter'
import FilterPill from './FilterPill'
import FilterInput from './FilterInput'
import MoreFilters from './MoreFilters'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import { pickFilters, withoutFilters } from './filterState'

const pickDefaults = (defaults) => JSON.parse(JSON.stringify(defaults))

const LOWER_BOUNDS = ['after', 'strictly_after', 'gt', 'gte']
const UPPER_BOUNDS = ['before', 'strictly_before', 'lt', 'lte']

export default {
    components: {
        SearchBox,
        ChipFilter,
        FilterPill,
        FilterInput,
        MoreFilters,
    },

    props: {
        value: {
            type: Object,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        busy: {
            type: Boolean,
        },
        hideFilterBox: {
            type: Boolean,
        },
        hideSearchBox: {
            type: Boolean,
        },
        // the search stays open at the start of the bar (merchant portal Orders "Search orders");
        // every list does this on desktop, false brings back the collapsible icon at the right end
        searchFirst: {
            type: Boolean,
            default: true,
        },
        listFilters: {
            type: Array,
            default: () => {
                return []
            },
        },
        // the filters the list opens with: "Clear all" goes back to them and is shown only while
        // the filters differ from them
        defaultFilters: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            tableFilters: {},
        }
    },

    computed: {
        // a list whose own search is a "_search" filter (invoices) shows it as the bar's search
        barSearch() {
            if (!this.hideSearchBox) return undefined
            return this.listFilters.find(
                (filter) =>
                    filter.inputType === 'search' && filter.key === '_search'
            )
        },
        // a "from" date filter directly followed by its "until" pair becomes one date range pill
        pillFilters() {
            const pills = []
            const filters = this.listFilters.filter(
                (filter) => filter !== this.barSearch
            )
            for (let i = 0; i < filters.length; i++) {
                const start = filters[i]
                const end = filters[i + 1]
                if (
                    start.inputType === 'date' &&
                    end?.inputType === 'date' &&
                    LOWER_BOUNDS.includes(start.type) &&
                    UPPER_BOUNDS.includes(end.type)
                ) {
                    pills.push({
                        inputType: 'daterange',
                        key: start.key,
                        type: start.type,
                        translatedLabel: this.rangeLabel(start, end),
                        advanced: start.advanced,
                        start,
                        end,
                    })
                    i++
                } else pills.push(start)
            }
            return pills
        },
        // filters marked `advanced` go into the More filters side panel
        barFilters() {
            return this.pillFilters.filter((filter) => !filter.advanced)
        },
        advancedFilters() {
            return this.pillFilters.filter((filter) => filter.advanced)
        },
        // the list's defaults plus the chips that switch themselves on (`default: true`, user
        // Budgets "Current balances"): those are part of the state the list opens with, so they
        // neither show "Clear all" on the first load nor get dropped by it (sweep 2026-11-04)
        openingFilters() {
            const result = pickDefaults(this.defaultFilters)
            const write = (key, type, value) => {
                if (value === undefined) return
                if (!type) {
                    result[key] = value
                    return
                }
                result[key] = { ...(result[key] || {}), [type]: value }
            }
            this.listFilters.forEach((filter) => {
                if (filter.inputType !== 'checkbox' || !filter.default) return
                write(filter.key, filter.type, filter.value)
                if (filter.other)
                    write(
                        filter.other.key,
                        filter.other.type,
                        filter.other.value
                    )
            })
            return result
        },
        filtered() {
            return !isEqual(
                pickFilters(this.tableFilters, this.pillFilters),
                pickFilters(this.openingFilters, this.pillFilters)
            )
        },
        clearAllText() {
            return this.$te('actions.clear_all')
                ? this.$t('actions.clear_all')
                : 'Clear all'
        },
    },

    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(value) {
                if (!isEqual(value, this.tableFilters)) {
                    this.tableFilters = value
                }
            },
        },
        tableFilters: {
            deep: true,
            handler(value) {
                this.$emit('input', value)
            },
        },
    },

    methods: {
        // every filter back to the list's defaults; the search, the order and the page size stay
        clearAll() {
            this.tableFilters = merge(
                withoutFilters(this.tableFilters, this.pillFilters),
                pickDefaults(this.openingFilters)
            )
        },
        // an active checkbox chip on the same field ("Upcoming" = delivery time after now) already
        // shows that value; the date pill of the field must not look applied as well
        chipOwns(filter) {
            if (!['date', 'daterange'].includes(filter.inputType)) return false
            const read = (key, type) =>
                type ? this.tableFilters[key]?.[type] : this.tableFilters[key]
            return this.listFilters.some(
                (chip) =>
                    chip.inputType === 'checkbox' &&
                    chip.key === filter.key &&
                    chip.value !== undefined &&
                    isEqual(read(chip.key, chip.type), chip.value) &&
                    (!chip.other ||
                        isEqual(
                            read(chip.other.key, chip.other.type),
                            chip.other.value
                        ))
            )
        },
        // "Delivery time from" + "Delivery time until" → "Delivery time"; two different fields
        // (invoice period start / end) keep both names
        rangeLabel(start, end) {
            if (start.rangeLabel) return start.rangeLabel
            const label = this.$t(start.label)
            if (start.key !== end.key) return `${label} – ${this.$t(end.label)}`
            return label.replace(/\s+(from|start|after)$/i, '')
        },
        pillMinWidth(filter) {
            // two months, one on a phone
            if (filter.inputType === 'daterange')
                return this.$vuetify.breakpoint.xsOnly ? 312 : 608
            // a single input needs room; an option list is as wide as its options
            if (filter.inputType === 'location') return 360
            return filter.inputType === 'select' ? undefined : 240
        },
    },
}
</script>
